import React, { createContext, useContext, useMemo } from "react";
import { isNil } from "@salespark/toolkit";
import { useAuth } from "context/Auth";

const PermissionsContext = createContext();
export { PermissionsContext }; //used for Class Component

const normalizePermissions = (authData) => {
  if (isNil(authData)) return [];

  const rawPermissions = authData?.permissions;

  if (Array.isArray(rawPermissions)) {
    const sanitizedPermissions = rawPermissions.filter((permission) => typeof permission === "string" && permission.trim().length > 0);

    return sanitizedPermissions.length > 0 ? sanitizedPermissions : [];
  }

  if (typeof rawPermissions === "string") {
    const sanitizedPermissions = rawPermissions
      .split(",")
      .map((permission) => permission.trim())
      .filter((permission) => permission.length > 0);

    return sanitizedPermissions.length > 0 ? sanitizedPermissions : [];
  }

  return [];
};

export default function PermissionsProvider({ children }) {
  const { auth } = useAuth();

  const permissions = useMemo(() => normalizePermissions(auth?.data), [auth?.data]);

  const value = useMemo(
    () => ({
      permissions,
      hasPermission: (permission) => permissions.includes(permission),
      permissionsList: () => permissions,
      hasAnyPermission: (permissionsToCheck = []) => (permissionsToCheck || []).some((permission) => permissions.includes(permission)),
      hasAllPermissions: (permissionsToCheck = []) => (permissionsToCheck || []).every((permission) => permissions.includes(permission)),
    }),
    [permissions],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) throw new Error("usePermissions must be used within a PermissionsProvider");
  return context;
}
export const userPermissions = (authData) => {
  const permissions = normalizePermissions(authData);
  return permissions;
};
